/**
 * burdenReferenceData.ts — State and trade reference rates for the Burden Builder.
 *
 * These are typical baseline rates an SMB contractor would pay. Sources:
 *  - FICA / FUTA: IRS, federal, fixed.
 *  - SUTA: state unemployment insurance — using new-employer default rates as a
 *    starting point. Real rates vary 0.5%–6%+ by experience rating. Users can
 *    override once they get their state rate notice.
 *  - Workers' Comp: state WC bureau rates per class code. We use a representative
 *    mid-range value for each (state × trade) pair. Real WC rates can vary 30%+
 *    based on company experience modifier, broker margin, and exact classification.
 *    The user can override the WC rate field directly.
 *
 * Accuracy disclaimer: these are STARTING POINTS, not authoritative tax/insurance
 * rates. The "computed burden" surfaced to the user always includes a note that
 * they should verify against their actual payroll/WC bills.
 */

import type { IndustryType } from './industryConfig';

// --- Federal (fixed) -------------------------------------------------------

/** Employer half of FICA — Social Security 6.2% + Medicare 1.45%. Fixed federally. */
export const FICA_PERCENT = 7.65;

/** FUTA — 0.6% net rate on first $7,000 of wages per employee. For a full-time
 *  worker that's ~$42/year, which on a typical $50k wage is ~0.084%. Round to
 *  0.1% as a usable burden contribution. */
export const FUTA_EFFECTIVE_PERCENT = 0.1;

// --- State SUTA (new-employer defaults; users can override) ----------------

/**
 * Tope salarial del SUTA por estado (2026), en dólares por empleado y año.
 *
 * ESTO ES LO QUE FALTABA. El SUTA no se paga sobre toda la nómina: se paga
 * sobre los primeros N dólares de CADA empleado y ahí se acaba. Aplicar el
 * porcentaje a la nómina entera —que es lo que se hacía— multiplica esa parte
 * de las cargas por tres o por siete.
 *
 * Un ejemplo real: un empleado de 60.000 $ en California, SUTA 3,4 %.
 *   correcto:  3,4 % × 7.000  =   238 $/año  (0,40 % del sueldo)
 *   como iba:  3,4 % × 60.000 = 2.040 $/año  (3,40 % del sueldo)
 *
 * El error se INVIERTE según el estado —es casi nulo en Washington (78.200 $)
 * y de 6× en California o Florida (7.000 $)— y por eso nunca se vio.
 *
 * El FUTA de aquí abajo ya lo hacía bien: su comentario convierte 0,6 % sobre
 * 7.000 $ en un 0,1 % efectivo. Esto es la misma idea aplicada al SUTA.
 */
export const STATE_SUTA_WAGE_BASE: Record<string, number> = {
  AL: 8_000,  AK: 51_700, AZ: 8_000,  AR: 7_000,  CA: 7_000,  CO: 27_200,
  CT: 26_100, DE: 12_500, DC: 9_000,  FL: 7_000,  GA: 9_500,  HI: 62_000,
  ID: 55_300, IL: 13_916, IN: 9_500,  IA: 39_500, KS: 14_000, KY: 11_700,
  LA: 7_700,  ME: 12_000, MD: 8_500,  MA: 15_000, MI: 9_500,  MN: 43_000,
  MS: 14_000, MO: 9_500,  MT: 45_100, NE: 9_000,  NV: 41_800, NH: 14_000,
  NJ: 43_300, NM: 31_700, NY: 12_800, NC: 32_600, ND: 45_100, OH: 9_000,
  OK: 27_000, OR: 54_300, PA: 10_000, RI: 29_800, SC: 14_000, SD: 15_000,
  TN: 7_000,  TX: 9_000,  UT: 48_900, VT: 14_800, VA: 8_000,  WA: 72_800,
  WV: 9_500,  WI: 14_000, WY: 32_400,
};

/** Media nacional aproximada, para cuando el taller no ha puesto su estado. */
export const DEFAULT_SUTA_WAGE_BASE = 15_000;

/** Tope del Seguro Social (6,2 %). El Medicare (1,45 %) no tiene tope. */
export const SOCIAL_SECURITY_WAGE_BASE = 184_500;
export const SOCIAL_SECURITY_PERCENT = 6.2;
export const MEDICARE_PERCENT = 1.45;

export function getSutaWageBase(state?: string): number {
  if (!state) return DEFAULT_SUTA_WAGE_BASE;
  return STATE_SUTA_WAGE_BASE[state.toUpperCase()] ?? DEFAULT_SUTA_WAGE_BASE;
}

export const STATE_SUTA_DEFAULTS: Record<string, number> = {
  AL: 2.70, AK: 2.74, AZ: 2.00, AR: 3.10, CA: 3.40, CO: 1.70, CT: 3.00, DE: 1.80,
  DC: 2.70, FL: 2.70, GA: 2.64, HI: 4.00, ID: 1.00, IL: 3.95, IN: 2.50, IA: 1.00,
  KS: 2.70, KY: 2.70, LA: 1.16, ME: 2.04, MD: 2.30, MA: 2.42, MI: 2.70, MN: 0.80,
  MS: 1.00, MO: 2.38, MT: 1.30, NE: 1.25, NV: 2.95, NH: 2.70, NJ: 2.80, NM: 1.00,
  NY: 4.10, NC: 1.00, ND: 1.13, OH: 2.70, OK: 1.50, OR: 2.40, PA: 3.69, RI: 0.98,
  SC: 0.55, SD: 1.20, TN: 2.70, TX: 2.70, UT: 1.40, VT: 1.00, VA: 2.50, WA: 1.00,
  WV: 2.70, WI: 3.05, WY: 1.22,
};

// --- Workers' Comp by state × trade ----------------------------------------

/** Approximate WC rates per $100 of payroll, expressed as a % of payroll.
 *  Each industry shows a mid-range for that trade. Range can vary ±50% by carrier. */
export const WC_RATES: Record<IndustryType, Record<string, number>> = {
  landscaping: {
    // Class 0042 Landscape Gardening / 0005 Farm
    AL: 8.5, AK: 11.0, AZ: 7.5, AR: 8.0, CA: 12.0, CO: 7.0, CT: 9.5, DE: 9.0,
    DC: 8.5, FL: 7.8, GA: 7.5, HI: 9.0, ID: 8.0, IL: 10.5, IN: 7.0, IA: 7.5,
    KS: 8.0, KY: 9.0, LA: 8.5, ME: 11.0, MD: 8.5, MA: 10.5, MI: 9.0, MN: 8.5,
    MS: 8.0, MO: 8.0, MT: 9.5, NE: 7.5, NV: 8.0, NH: 9.5, NJ: 10.0, NM: 8.5,
    NY: 15.0, NC: 8.0, ND: 6.0, OH: 7.5, OK: 8.0, OR: 9.5, PA: 9.5, RI: 11.5,
    SC: 9.0, SD: 7.5, TN: 8.0, TX: 8.5, UT: 7.0, VT: 9.0, VA: 7.5, WA: 11.0,
    WV: 8.5, WI: 9.0, WY: 8.0,
  },
  general_contractor: {
    // Class 5645 Carpentry / 5403 Carpentry mid-range
    AL: 10.0, AK: 13.5, AZ: 9.5, AR: 10.0, CA: 14.5, CO: 9.0, CT: 12.0, DE: 11.5,
    DC: 11.0, FL: 10.0, GA: 10.0, HI: 11.5, ID: 10.0, IL: 14.0, IN: 9.0, IA: 9.5,
    KS: 10.0, KY: 11.0, LA: 10.5, ME: 14.0, MD: 11.0, MA: 13.0, MI: 12.0, MN: 11.0,
    MS: 10.5, MO: 10.5, MT: 12.0, NE: 9.5, NV: 10.5, NH: 12.0, NJ: 13.0, NM: 11.0,
    NY: 18.0, NC: 10.0, ND: 7.5, OH: 9.5, OK: 10.0, OR: 12.0, PA: 12.0, RI: 14.5,
    SC: 11.5, SD: 9.5, TN: 10.0, TX: 10.5, UT: 9.0, VT: 11.5, VA: 9.5, WA: 13.5,
    WV: 11.0, WI: 11.5, WY: 10.0,
  },
  concrete: {
    // Class 5213 Concrete Construction
    AL: 11.0, AK: 14.0, AZ: 10.5, AR: 11.0, CA: 16.0, CO: 10.0, CT: 13.0, DE: 12.5,
    DC: 12.0, FL: 11.5, GA: 11.0, HI: 12.5, ID: 11.0, IL: 15.0, IN: 10.0, IA: 10.5,
    KS: 11.0, KY: 12.0, LA: 11.5, ME: 15.0, MD: 12.0, MA: 14.0, MI: 13.0, MN: 12.0,
    MS: 11.5, MO: 11.5, MT: 13.0, NE: 10.5, NV: 11.5, NH: 13.0, NJ: 14.0, NM: 12.0,
    NY: 19.5, NC: 11.0, ND: 8.5, OH: 10.5, OK: 11.0, OR: 13.0, PA: 13.0, RI: 15.5,
    SC: 12.5, SD: 10.5, TN: 11.0, TX: 11.5, UT: 10.0, VT: 12.5, VA: 10.5, WA: 14.5,
    WV: 12.0, WI: 12.5, WY: 11.0,
  },
  electrical: {
    // Class 5190 Electrical Wiring
    AL: 5.5, AK: 7.5, AZ: 5.0, AR: 5.5, CA: 8.0, CO: 5.0, CT: 6.5, DE: 6.0,
    DC: 6.0, FL: 5.5, GA: 5.5, HI: 6.5, ID: 5.5, IL: 8.0, IN: 5.0, IA: 5.0,
    KS: 5.5, KY: 6.0, LA: 5.5, ME: 7.5, MD: 6.0, MA: 7.0, MI: 6.5, MN: 6.0,
    MS: 5.5, MO: 5.5, MT: 6.5, NE: 5.0, NV: 5.5, NH: 6.5, NJ: 7.0, NM: 6.0,
    NY: 10.0, NC: 5.5, ND: 4.0, OH: 5.0, OK: 5.5, OR: 6.5, PA: 6.5, RI: 8.0,
    SC: 6.0, SD: 5.0, TN: 5.5, TX: 5.5, UT: 4.5, VT: 6.0, VA: 5.0, WA: 7.0,
    WV: 6.0, WI: 6.0, WY: 5.5,
  },
  plumbing: {
    // Class 5183 Plumbing
    AL: 6.5, AK: 8.5, AZ: 6.0, AR: 6.5, CA: 9.0, CO: 6.0, CT: 7.5, DE: 7.0,
    DC: 7.0, FL: 6.5, GA: 6.5, HI: 7.5, ID: 6.5, IL: 9.0, IN: 6.0, IA: 6.0,
    KS: 6.5, KY: 7.0, LA: 6.5, ME: 8.5, MD: 7.0, MA: 8.0, MI: 7.5, MN: 7.0,
    MS: 6.5, MO: 6.5, MT: 7.5, NE: 6.0, NV: 6.5, NH: 7.5, NJ: 8.0, NM: 7.0,
    NY: 11.0, NC: 6.5, ND: 5.0, OH: 6.0, OK: 6.5, OR: 7.5, PA: 7.5, RI: 9.0,
    SC: 7.0, SD: 6.0, TN: 6.5, TX: 6.5, UT: 5.5, VT: 7.0, VA: 6.0, WA: 8.0,
    WV: 7.0, WI: 7.0, WY: 6.5,
  },
  painting: {
    // Class 5474 Painting NOC
    AL: 9.0, AK: 11.5, AZ: 8.5, AR: 9.0, CA: 12.5, CO: 8.0, CT: 10.0, DE: 9.5,
    DC: 9.5, FL: 9.0, GA: 8.5, HI: 10.0, ID: 9.0, IL: 11.5, IN: 8.0, IA: 8.5,
    KS: 9.0, KY: 9.5, LA: 9.0, ME: 11.5, MD: 9.5, MA: 11.0, MI: 10.0, MN: 9.5,
    MS: 9.0, MO: 9.0, MT: 10.5, NE: 8.5, NV: 9.0, NH: 10.0, NJ: 11.0, NM: 9.5,
    NY: 15.5, NC: 9.0, ND: 6.5, OH: 8.5, OK: 9.0, OR: 10.5, PA: 10.5, RI: 12.0,
    SC: 9.5, SD: 8.5, TN: 9.0, TX: 9.0, UT: 8.0, VT: 9.5, VA: 8.5, WA: 11.5,
    WV: 9.5, WI: 9.5, WY: 9.0,
  },
  cleaning: {
    // Class 9014 Janitorial Services
    AL: 4.5, AK: 6.0, AZ: 4.0, AR: 4.5, CA: 6.5, CO: 4.0, CT: 5.5, DE: 5.0,
    DC: 5.0, FL: 4.5, GA: 4.5, HI: 5.5, ID: 4.5, IL: 6.5, IN: 4.0, IA: 4.0,
    KS: 4.5, KY: 5.0, LA: 4.5, ME: 6.0, MD: 5.0, MA: 5.5, MI: 5.0, MN: 5.0,
    MS: 4.5, MO: 4.5, MT: 5.5, NE: 4.0, NV: 4.5, NH: 5.5, NJ: 5.5, NM: 5.0,
    NY: 8.5, NC: 4.5, ND: 3.5, OH: 4.0, OK: 4.5, OR: 5.5, PA: 5.5, RI: 6.5,
    SC: 5.0, SD: 4.0, TN: 4.5, TX: 4.5, UT: 4.0, VT: 5.0, VA: 4.0, WA: 6.0,
    WV: 5.0, WI: 5.0, WY: 4.5,
  },
  hvac: {
    // Class 5537 Heating/AC Installation
    AL: 6.5, AK: 8.5, AZ: 6.0, AR: 6.5, CA: 9.0, CO: 6.0, CT: 7.5, DE: 7.0,
    DC: 7.0, FL: 6.5, GA: 6.5, HI: 7.5, ID: 6.5, IL: 9.0, IN: 6.0, IA: 6.0,
    KS: 6.5, KY: 7.0, LA: 6.5, ME: 8.5, MD: 7.0, MA: 8.0, MI: 7.5, MN: 7.0,
    MS: 6.5, MO: 6.5, MT: 7.5, NE: 6.0, NV: 6.5, NH: 7.5, NJ: 8.0, NM: 7.0,
    NY: 11.0, NC: 6.5, ND: 5.0, OH: 6.0, OK: 6.5, OR: 7.5, PA: 7.5, RI: 9.0,
    SC: 7.0, SD: 6.0, TN: 6.5, TX: 6.5, UT: 5.5, VT: 7.0, VA: 6.0, WA: 8.0,
    WV: 7.0, WI: 7.0, WY: 6.5,
  },
  roofing: {
    // Class 5551 Roofing — highest WC class
    AL: 22.0, AK: 30.0, AZ: 20.0, AR: 22.0, CA: 35.0, CO: 20.0, CT: 28.0, DE: 25.0,
    DC: 25.0, FL: 22.0, GA: 22.0, HI: 28.0, ID: 22.0, IL: 32.0, IN: 20.0, IA: 22.0,
    KS: 22.0, KY: 25.0, LA: 22.0, ME: 30.0, MD: 25.0, MA: 30.0, MI: 28.0, MN: 25.0,
    MS: 22.0, MO: 22.0, MT: 28.0, NE: 22.0, NV: 22.0, NH: 28.0, NJ: 30.0, NM: 25.0,
    NY: 45.0, NC: 22.0, ND: 18.0, OH: 22.0, OK: 22.0, OR: 28.0, PA: 28.0, RI: 32.0,
    SC: 25.0, SD: 22.0, TN: 22.0, TX: 22.0, UT: 20.0, VT: 25.0, VA: 22.0, WA: 30.0,
    WV: 25.0, WI: 25.0, WY: 22.0,
  },
  custom: {
    // Generic baseline — used when industry = custom or unrecognized
    AL: 7.0, AK: 9.5, AZ: 6.5, AR: 7.0, CA: 10.5, CO: 6.5, CT: 8.5, DE: 8.0,
    DC: 8.0, FL: 7.5, GA: 7.0, HI: 8.5, ID: 7.0, IL: 9.5, IN: 6.5, IA: 7.0,
    KS: 7.0, KY: 7.5, LA: 7.5, ME: 9.5, MD: 8.0, MA: 9.0, MI: 8.0, MN: 7.5,
    MS: 7.0, MO: 7.0, MT: 8.5, NE: 6.5, NV: 7.0, NH: 8.5, NJ: 9.0, NM: 7.5,
    NY: 13.0, NC: 7.0, ND: 5.5, OH: 6.5, OK: 7.0, OR: 8.5, PA: 8.5, RI: 10.0,
    SC: 8.0, SD: 6.5, TN: 7.0, TX: 7.5, UT: 6.0, VT: 8.0, VA: 6.5, WA: 9.5,
    WV: 7.5, WI: 8.0, WY: 7.0,
  },
};

export const US_STATES: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' },       { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },       { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },    { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },   { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },       { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },         { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },       { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },        { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },     { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },      { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },      { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },   { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },       { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },        { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },    { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },      { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },      { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },{ code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },     { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },          { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },      { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

export function getSutaRate(state?: string): number {
  if (!state) return 2.7; // national average fallback
  return STATE_SUTA_DEFAULTS[state.toUpperCase()] ?? 2.7;
}

export function getWcRate(industry: IndustryType | string | undefined, state?: string): number {
  const ind = (industry as IndustryType) || 'custom';
  const stateMap = WC_RATES[ind] ?? WC_RATES.custom;
  if (!state) {
    // National-average fallback: mid-range for the trade
    const values = Object.values(stateMap);
    return values.reduce((a, b) => a + b, 0) / values.length;
  }
  return stateMap[state.toUpperCase()] ?? Object.values(stateMap).reduce((a, b) => a + b, 0) / Object.values(stateMap).length;
}
